import { useGetGlobalAttributes } from '@elliemae/ds-props-helpers';
import React from 'react';
import { LeftNavContext } from '../../LeftNavigationContext.js';
import { LEFT_NAVIGATION_DATA_TESTID } from '../../constants/index.js';
import { ItemSkeleton } from '../../outOfTheBox/ItemSkeleton/index.js';
import { LeftNavFooterItem } from '../LeftNavFooterItem/index.js';
import { LeftNavLoading } from '../LeftNavLoading/index.js';
import { OutOfTheBoxMapItem } from '../OutOfTheBoxMapItem.js';
import {
  StyledLeftNavAreasContainer,
  StyledLeftNavBodyAreasContainer,
  StyledLeftNavBodyHeaderArea,
  StyledLeftNavBodyItemsArea,
  StyledLeftNavExpandAnimationWrapper,
  StyledLeftNavHeaderArea,
} from './styled.js';
const leftNavScreenReaderInstructions =
  'Left Navigation. You can navigate through the items using the Up/Down arrows. ' +
  'To expand or select an item, use the Enter/Return or Space keys. ' +
  'Expandable items can also be opened/closed with the Right/Left arrows. ' +
  'The Home and End keys will take you to the first and last item respectively.';

const LeftNavInnerContent = () => {
  const ctx = React.useContext(LeftNavContext);
  const {
    leftNavProps,
    leftNavProps: { HeaderComponent, BodyHeaderComponent, openedItem, items, withoutBodyShadow, isSkeleton },
    selectedItem,
  } = ctx;
  const getOwnerProps = React.useCallback(() => leftNavProps, [leftNavProps]);
  const getOwnerPropsArguments = React.useCallback(() => ({}), []);
  return (
    <>
      <StyledLeftNavHeaderArea
        selected={!!HeaderComponent && [selectedItem, openedItem].includes(HeaderComponent.dsId as string)}
        data-testid={LEFT_NAVIGATION_DATA_TESTID.HEADER_AREA} // https://jira.elliemae.io/browse/PUI-15589
        role="group"
        getOwnerProps={getOwnerProps}
        getOwnerPropsArguments={getOwnerPropsArguments}
      >
        {isSkeleton ? (
          <ItemSkeleton
            ctx={ctx}
            item={{ dsId: 'header-skeleton', itemOpts: { skeletonVariant: 'item' } }}
            hasBorderBottom
          />
        ) : (
          <> {HeaderComponent ? <OutOfTheBoxMapItem item={HeaderComponent} /> : null}</>
        )}
      </StyledLeftNavHeaderArea>
      <StyledLeftNavBodyAreasContainer
        cols={['1fr']}
        rows={['auto', '1fr']}
        role="group"
        data-testid={LEFT_NAVIGATION_DATA_TESTID.BODY_AREAS_CONTAINER} // https://jira.elliemae.io/browse/PUI-15589
        getOwnerProps={getOwnerProps}
        getOwnerPropsArguments={getOwnerPropsArguments}
      >
        <StyledLeftNavBodyHeaderArea
          selected={!!BodyHeaderComponent && [selectedItem, openedItem].includes(BodyHeaderComponent.dsId as string)}
          role="group"
          isSkeleton={isSkeleton}
          data-testid={LEFT_NAVIGATION_DATA_TESTID.BODY_HEADER_AREA} // https://jira.elliemae.io/browse/PUI-15589
          getOwnerProps={getOwnerProps}
          getOwnerPropsArguments={getOwnerPropsArguments}
        >
          {isSkeleton ? (
            <ItemSkeleton
              ctx={ctx}
              item={{
                dsId: 'body-header-skeleton',
                itemOpts: {
                  skeletonVariant: 'item',
                },
              }}
            />
          ) : null}
          {!isSkeleton && BodyHeaderComponent ? <OutOfTheBoxMapItem item={BodyHeaderComponent} /> : null}
        </StyledLeftNavBodyHeaderArea>
        <StyledLeftNavBodyItemsArea
          selected={!withoutBodyShadow && openedItem !== null}
          tabIndex={-1}
          role="group"
          isSkeleton={isSkeleton}
          data-testid={LEFT_NAVIGATION_DATA_TESTID.BODY_ITEMS_AREA} // https://jira.elliemae.io/browse/PUI-15589
          getOwnerProps={getOwnerProps}
          getOwnerPropsArguments={getOwnerPropsArguments}
        >
          {isSkeleton ? (
            <ItemSkeleton
              ctx={ctx}
              item={{
                dsId: 'action-items-skeleton',
                itemOpts: {
                  skeletonVariant: 'subitem',
                },
              }}
            />
          ) : (
            <>
              {items.map((item) => (
                <OutOfTheBoxMapItem item={item} key={item.dsId} />
              ))}
            </>
          )}
        </StyledLeftNavBodyItemsArea>
      </StyledLeftNavBodyAreasContainer>
    </>
  );
};

// original props from previous dev
const LeftNavContent: React.ComponentType = () => {
  const {
    expandedForAnimation,
    leftNavProps: {
      expanded,
      expandedWidth,
      FooterLabelComponent,
      footerLabel,
      onFooterExpand,
      onFooterClose,
      loading,
    },
    leftNavProps,
  } = React.useContext(LeftNavContext);

  const getOwnerProps = React.useCallback(() => leftNavProps, [leftNavProps]);

  const getOwnerPropsArguments = React.useCallback(() => ({}), []);

  const globalProps = useGetGlobalAttributes(leftNavProps);

  return (
    <StyledLeftNavExpandAnimationWrapper
      expandedWidth={expandedWidth}
      expanded={expanded}
      aria-label={leftNavScreenReaderInstructions}
      data-testid={LEFT_NAVIGATION_DATA_TESTID.ROOT} // https://jira.elliemae.io/browse/PUI-15589
      {...globalProps}
      getOwnerProps={getOwnerProps}
      getOwnerPropsArguments={getOwnerPropsArguments}
    >
      <StyledLeftNavAreasContainer
        expandedWidth={expandedWidth}
        expanded={expandedForAnimation}
        rows={loading ? ['1fr', 'auto'] : ['auto', '1fr', 'auto']}
        cols={['1fr']}
        role="menu"
        data-testid={LEFT_NAVIGATION_DATA_TESTID.AREAS_CONTAINER} // https://jira.elliemae.io/browse/PUI-15589
        getOwnerProps={getOwnerProps}
        getOwnerPropsArguments={getOwnerPropsArguments}
      >
        {loading && <LeftNavLoading expanded={expandedForAnimation} />}
        {!loading && <LeftNavInnerContent />}
        <LeftNavFooterItem
          footerLabel={footerLabel}
          FooterLabelComponent={FooterLabelComponent}
          onFooterExpand={onFooterExpand}
          onFooterClose={onFooterClose}
        />
      </StyledLeftNavAreasContainer>
    </StyledLeftNavExpandAnimationWrapper>
  );
};

export default LeftNavContent;
